import { cva, type VariantProps } from 'class-variance-authority';

/**
 * Sidebar menu item visual variants.
 *
 * Figma "Flashka Design System" file `YZK9r3wDhvw1pY60KlVEIj`, component set
 * "Side bar menu item updated" — light `5311:5347`, dark `5326:39356`.
 *
 * Colour states (light hex → the CSS-var palette auto-inverts in dark mode,
 * verified against the dark frame's resolved variables):
 *
 * Every fill spans the WHOLE row — no sub-element (chevron, slots) carries a
 * state fill of its own.
 *
 *   default     transparent, label secondary-600 (#525252 → #a1a1a1)
 *   hover       bg secondary-100 (#f5f5f5 → #171717) — node 5311:6492
 *   pressed     bg secondary-200 (#e5e5e5 → #262626) — node 5311:6524
 *   active      bg secondary-100 + label secondary-800 (#262626 → #e5e5e5)
 *               — node 5311:6500
 *   focus       1px border secondary-600 (#525252 → #a1a1a1)
 *   disabled    opacity 70% (Figma 5311:6484 — this component uses 0.7, not
 *               the Button's 0.3)
 *   pre-highlighted            bg primary-50/50 + border primary-200 +
 *                              label primary-900 (`highlight="primary"`)
 *   pre-highlighted secondary  bg secondary-50 + border secondary-200
 *                              (`highlight="secondary"`)
 */
export const sidebarMenuItemVariants = cva(
  // Always-on transparent border keeps geometry stable when the focus /
  // pre-highlighted states swap in a coloured 1px border (Figma boxes are
  // border-box: total height stays 56/48/44/32).
  // `shrink-0` is load-bearing, not defensive: a row is normally a child of a
  // scrolling flex column, where the default `flex-shrink: 1` squashes `h-*`
  // down toward min-content once the column overflows. That made rows render
  // short AND grow on hover — unhiding `hoverRightSlot` (a `min-h-9` Button)
  // raises min-content, so the row visibly resized under the pointer.
  'group relative shrink-0 flex flex-row items-center gap-2 border border-transparent ' +
    'web:transition-[background-color,border-color,color,opacity,transform] ' +
    'web:duration-100 web:ease-out ' +
    // Focus state is the 1px secondary-600 border (Figma 5311:6476), not the
    // Pressable primitive's default ring — suppress that.
    'web:focus-visible:ring-0 web:focus-visible:ring-offset-0 ' +
    'web:focus-visible:border-secondary-600',
  {
    variants: {
      size: {
        // h · radius · pl/pr per Figma size bands (XL 5311:5348, L 5311:5908,
        // M 5311:6468, S 5311:7020). `pl` is measured to the chevron button
        // ("Slot 5"), `pr` to the right edge of the trailing slot.
        xl: 'h-14 rounded-[18px] pl-2.5 pr-3.5',
        l: 'h-12 rounded-[18px] pl-2 pr-2.5',
        m: 'h-11 rounded-[14px] pl-2 pr-2.5',
        s: 'h-9 rounded-[12px] pl-1.5 pr-2',
      },
      active: {
        true: 'bg-secondary-100',
        false: '',
      },
      highlight: {
        none: '',
        primary: 'bg-primary-50/50 border-primary-200',
        secondary: 'bg-secondary-50 border-secondary-200',
      },
      disabled: {
        // `!` beats the Pressable primitive's `disabled:opacity-50` fallback.
        true: '!opacity-70 web:pointer-events-none',
        false: '',
      },
      fullWidth: {
        true: 'w-full',
        false: '',
      },
    },
    compoundVariants: [
      // Hover / pressed washes apply to a resting row AND to a
      // pre-highlighted-SECONDARY one: its secondary-50 fill is a hair off the
      // panel, so without them the row reads as dead (the sidebar account chip
      // had no hover at all). Only `highlight="primary"` — a route-active or
      // drop-target row — keeps its own fill untouched, and an `active` row
      // already sits at the hover shade. The pressed wash is mirrored with
      // `native:active:*` the way MenuListItem does it. The hover wash is
      // UNprefixed so an Apple Pencil held over an iPad sidebar row shows it
      // too — the native Pressable reports hover for pen/mouse only.
      {
        active: false,
        highlight: 'none',
        disabled: false,
        class:
          'hover:bg-secondary-100 web:active:bg-secondary-200 ' +
          'native:active:bg-secondary-200',
      },
      {
        active: false,
        highlight: 'secondary',
        disabled: false,
        class:
          'hover:bg-secondary-100 web:active:bg-secondary-200 ' +
          'native:active:bg-secondary-200',
      },
      // An ACTIVE pre-highlighted-secondary row (the sidebar account chip on
      // its own route, 2026-09-15) sits at the hover shade: the `active` and
      // `highlight` variants both set a `bg-*`, and the class merge keeps the
      // later `highlight` one — so the active fill has to be restated here,
      // where compound classes win.
      {
        active: true,
        highlight: 'secondary',
        class: 'bg-secondary-100',
      },
    ],
    defaultVariants: {
      size: 'm',
      active: false,
      highlight: 'none',
      disabled: false,
      fullWidth: true,
    },
  },
);

/**
 * The drop-target cue (`dropTarget`) on web: the row grows toward the carried
 * item and nothing else — the same call as folder tiles' `folderDropCueClass`
 * (owner, 2026-10-15: the primary tint read as too much). From the LEFT edge,
 * so the tree's indent and chevron column hold still and only the label side
 * reaches out (the sidebar is always LTR); 3% on a ~230px row is a few px — a
 * nudge, not a jump. Rides the row's own 100ms transition, and is not gated on
 * reduced motion: it is the ONLY cue, and a 3% size step is not travel.
 *
 * Native keeps the primary tint instead (`SidebarMenuItem`): NativeWind
 * upgrades a Pressable carrying transition / transform utilities to a
 * Reanimated host, which crashes on RN 0.83 + Hermes (see
 * `SegmentedButton.variants.ts`).
 */
export const sidebarMenuItemDropCueClass = (over: boolean) =>
  over ? 'web:origin-left web:scale-[1.03]' : '';

/**
 * Label classes per size + state. Typography per Figma: XL = H2 (20/28
 * SemiBold), L = H3 (18/26 SemiBold), M/S = B2 (14/20 Medium).
 *
 * M deliberately does NOT use the component set's own H4: the sidebar
 * composition (5871:50374) — the only place this component ships — sets EVERY
 * row label in B2 14/20, whatever the row height, and H4 reads visibly too big
 * against it.
 *
 * `leading-5` restores Figma's 20px: `typography.json` still carries a stale
 * 22px line-height for `b2` (the specimen 6:339 says 20). Same drift affects
 * `b3` (16, not 20) and `h4` (22, not 24) — fixing the tokens is a separate,
 * app-wide change.
 */
export const sidebarMenuItemTextVariants = cva('text-left', {
  variants: {
    size: {
      xl: 'text-h2 font-semibold native:font-mona-semibold',
      l: 'text-h3 font-semibold native:font-mona-semibold',
      m: 'text-b2 font-medium leading-5 native:font-mona-medium',
      s: 'text-b2 font-medium leading-5 native:font-mona-medium',
    },
    active: {
      true: 'text-secondary-800',
      false: 'text-secondary-600',
    },
    // Declared after `active` so `cn`'s twMerge keeps the highlight colour
    // when both are set.
    highlight: {
      none: '',
      primary: 'text-primary-900',
      secondary: '',
    },
  },
  defaultVariants: { size: 'm', active: false, highlight: 'none' },
});

/**
 * The chevron icon-button (Figma "Slot 5" → Icon Button): 32px on XL, 28px on
 * L/M/S. Keep `CHEVRON_BUTTON_PX` in `SidebarMenuItem.icons.ts` in step.
 *
 * It carries NO fill of its own in ANY state — every frame of the component
 * set (5311:5347) paints hover / pressed / active across the WHOLE row, and a
 * private chevron chip made the row look like the only hoverable thing was the
 * chevron. `-mr-2` cancels the row's `gap-2` so the button sits flush against
 * the left icon, per Figma (content frame starts exactly at pl + button).
 */
export const sidebarMenuItemChevronVariants = cva(
  // Keyboard focus still needs a visible target of its own — it lands on the
  // chevron alone, not the row — so `focus-visible` keeps a fill. Hover and
  // press deliberately do not.
  'flex shrink-0 flex-row items-center justify-center -mr-2 ' +
    'web:focus-visible:ring-0 web:focus-visible:ring-offset-0 ' +
    'web:focus-visible:bg-secondary-200',
  {
    variants: {
      size: {
        xl: 'size-8 rounded-[12px]',
        l: 'size-7 rounded-[10px]',
        m: 'size-7 rounded-[10px]',
        s: 'size-7 rounded-[10px]',
      },
    },
    defaultVariants: { size: 'm' },
  },
);

export type SidebarMenuItemVariantProps = VariantProps<typeof sidebarMenuItemVariants>;
